import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { markRunbookSteps, ticketRunbooksQuery, type TicketRunbook } from '../api/runbooks'
import { failed } from '../applications/parts'
import { RunbookLevels } from '../applications/AppRunbook'
import { toast } from '../ui/notify'
import '../applications/AppRunbook.css'

/**
 * Sus in fila "IT Solutions" a tichetului: runbook-ul aplicatiei de business.
 *
 * Cand un aparat (sau CI) al tichetului e componenta unei aplicatii care are
 * runbook - o solutie IT Solutions legata de aplicatie - pasii ei stau aici,
 * pe niveluri, cu bife. "Save ticked steps" scrie pe tichet o nota interna
 * ("Runbook steps 1-3 done by X"), nu o stare tinuta separat.
 *
 * Pe un astfel de tichet fila IT Solutions vine imediat dupa Summary
 * (`TicketTabs::solutionsUpFront`), deci e pe rand, nu in meniul filelor.
 *
 * Fara aplicatii cu runbook, fara drept sau inainte de migrare, nu se
 * deseneaza nimic: fila ramane biblioteca de solutii, ca inainte.
 */
export function TicketSolutionRunbooks({ id }: { id: number }) {
  const { data } = useQuery({ ...ticketRunbooksQuery(id), retry: false })

  if (!data || !data.ready || !Array.isArray(data.runbooks) || data.runbooks.length === 0) return null

  return (
    <div className="tsol-runbooks">
      {data.runbooks.map((rb) => (
        <TicketRunbookCard key={rb.appId} ticketId={id} runbook={rb} canMark={data.canMark} />
      ))}
    </div>
  )
}

function TicketRunbookCard({ ticketId, runbook, canMark }: { ticketId: number; runbook: TicketRunbook; canMark: boolean }) {
  const queryClient = useQueryClient()
  const [ticked, setTicked] = useState<Set<number>>(new Set())
  const [busy, setBusy] = useState(false)

  const toggle = (n: number) =>
    setTicked((all) => {
      const next = new Set(all)
      if (next.has(n)) next.delete(n)
      else next.add(n)
      return next
    })

  const save = () => {
    const steps = [...ticked].sort((a, b) => a - b)
    if (steps.length === 0) {
      toast('Tick at least one step.', 'Runbook', 'error')
      return
    }
    setBusy(true)
    markRunbookSteps(ticketId, runbook.appId, steps, runbook.base)
      .then((r) => {
        toast(r.message, 'Runbook', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') {
          setTicked(new Set())
          void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId, 'comments'] })
          void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId, 'changes'] })
          /* Un pas bifat e si un eveniment nou in "History" - numarul filei. */
          void queryClient.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId, 'tab-counts'] })
        }
      })
      .catch((error) => toast(failed(error), 'Runbook', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <section className="panel panel-default tsol-runbook" data-app={runbook.appId}>
      <div className="panel-heading">
        <i className="fas fa-list-ol" aria-hidden="true"></i> <strong>Runbook - {runbook.appName}</strong>
        <span className="adm-muted small"> · {runbook.title}</span>
      </div>
      <RunbookLevels
        runbook={runbook}
        tick={(s) =>
          canMark ? (
            <label className={`rb-tick${ticked.has(s.n) ? ' is-ticked' : ''}`}>
              <input type="checkbox" checked={ticked.has(s.n)} onChange={() => toggle(s.n)} aria-label={`Step ${s.n} done`} />
              <span className="rb-text">{s.text}</span>
            </label>
          ) : (
            <span className="rb-text">{s.text}</span>
          )
        }
      />
      <div className="rb-meta">
        {canMark ? (
          <>
            <span className="adm-muted small">Ticked steps are saved as an internal note on the ticket.</span>
            <button type="button" className="btn btn-primary btn-xs tsol-runbook-save" style={{ marginLeft: 'auto' }} disabled={busy || ticked.size === 0} onClick={save}>
              Save ticked steps
            </button>
          </>
        ) : (
          <span className="adm-muted small">You can read the steps; ticking them needs the right to edit tickets.</span>
        )}
      </div>
    </section>
  )
}
